import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";

interface DropdownOption<T> {
  label: string;
  value: T;
}

interface DropdownSelectProps<T> {
  options: DropdownOption<T>[];
  defaultValue?: string;
  placeholder?: string;
  onChange?: (value: T) => void;
  label?: string;
}

const DropdownContainer = styled.div`
  position: relative;
  min-width: 150px;
  font-family: "Manrope", sans-serif;
`;

const DropdownLabel = styled.div`
  font-size: 14px;
  color: #666;
  margin-bottom: 6px;
  font-family: "Manrope", sans-serif;
`;

const DropdownHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  background: #ffffff;
  cursor: pointer;
  font-size: 14px;
  color: #333;

  &:hover {
    border-color: #5575f7;
  }
`;

const DropdownListContainer = styled.div`
  position: absolute;
  z-index: 100;
  width: 100%;
  margin-top: 4px;
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
  max-height: 250px;
  overflow-y: auto;
`;

const DropdownList = styled.ul`
  padding: 0;
  margin: 0;
  list-style-type: none;
`;

const DropdownItem = styled.li`
  padding: 10px 12px;
  cursor: pointer;
  font-size: 14px;

  &:hover {
    background-color: #f5f8ff;
    color: #5575f7;
  }
`;

const SelectedValue = styled.div`
  font-size: 14px;
`;

const ArrowIcon = styled.div<{ isOpen: boolean }>`
  width: 5px;
  height: 5px;
  border-left: 2px solid #666;
  border-bottom: 2px solid #666;
  transform: ${(props) => (props.isOpen ? "rotate(135deg)" : "rotate(-45deg)")};
  margin-left: 8px;
  transition: transform 0.2s ease;
`;

const SearchIcon = styled.div`
  width: 16px;
  height: 16px;
  position: relative;

  &:before {
    content: "";
    position: absolute;
    width: 10px;
    height: 10px;
    border: 1.5px solid #666;
    border-radius: 50%;
    top: 0;
    left: 0;
  }

  &:after {
    content: "";
    position: absolute;
    width: 6px;
    height: 1.5px;
    background: #666;
    transform: rotate(45deg);
    top: 10px;
    left: 8px;
  }
`;

export const SearchDropdown = <T,>({
  options,
  defaultValue = "",
  placeholder = "Search...",
  onChange,
}: DropdownSelectProps<T>) => {
  const [searchValue, setSearchValue] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState(options);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);

    const filtered = options.filter((option) =>
      option.label.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredOptions(filtered);

    if (onChange) {
      onChange(value as T);
    }
  };

  return (
    <DropdownContainer>
      <DropdownHeader style={{ padding: 0 }}>
        <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
          <SearchIcon style={{ margin: "0 8px" }} />
          <input
            style={{
              border: "none",
              outline: "none",
              padding: "8px 0",
              width: "100%",
              fontSize: "14px",
              fontFamily: "Manrope, sans-serif",
              
            }}
            value={searchValue}
            onChange={handleSearch}
            placeholder={placeholder}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setTimeout(() => setIsFocused(false), 200)}
          />
        </div>
      </DropdownHeader>

      {isFocused && filteredOptions.length > 0 && (
        <DropdownListContainer>
          <DropdownList>
            {filteredOptions.map((option, index) => (
              <DropdownItem
                key={`${option.value}-${index}`}
                onClick={() => {
                  setSearchValue(option.label);
                  if (onChange) {
                    onChange(option.value);
                  }
                }}
              >
                {option.label}
              </DropdownItem>
            ))}
          </DropdownList>
        </DropdownListContainer>
      )}
    </DropdownContainer>
  );
};

export const FilterDropdown = <T,>({
  options,
  defaultValue = "",
  placeholder = "Select...",
  onChange,
  label,
}: DropdownSelectProps<T>) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState<DropdownOption<T> | null>(
    options.find((option) => option.value === defaultValue) || null
  );
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleDropdown = () => setIsOpen(!isOpen);

  const handleSelect = (option: DropdownOption<T>) => {
    setSelectedOption(option);
    setIsOpen(false);
    if (onChange) {
      onChange(option.value);
    }
  };

  return (
    <DropdownContainer ref={dropdownRef}>
      {label && <DropdownLabel>{label}</DropdownLabel>}
      <DropdownHeader onClick={toggleDropdown}>
        <SelectedValue>
          {selectedOption ? selectedOption.label : placeholder}
        </SelectedValue>
        <ArrowIcon isOpen={isOpen} />
      </DropdownHeader>

      {isOpen && (
        <DropdownListContainer>
          <DropdownList>
            {options.map((option, index) => (
              <DropdownItem
                key={`${option.value}-${index}`}
                onClick={() => handleSelect(option)}
              >
                {option.label}
              </DropdownItem>
            ))}
          </DropdownList>
        </DropdownListContainer>
      )}
    </DropdownContainer>
  );
};
